import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowLeftIcon, CircleCheckIcon, PlayIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams, Link } from 'react-router';

import PlainFieldset from '@/components/atoms/Fieldset/templates/PlainFieldset';
import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import Table from '@/components/atoms/Table/Table';
import { TableInfiniteLoader } from '@/components/organisms/InfiniteLoader/TableInfiniteLoader';
import {
  PageSubHeader,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader/PageSubHeader';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { useModalTryIt } from '@/hooks/useModalTryIt';
import { ChannelErrorTooltip } from '@/modules/campaigns/components/ChannelErrorTooltip';
import { selectAccountSlug, selectBroadcastId } from '@/redux/session/selectors';
import { Subscriber } from '@/shared/types/broadcast';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { formatDate, isOlderThan } from '@/util/dateUtils';
import { clampPercentage } from '@/util/limits';

import BroadcastLayout from '../components/BroadcastLayout/BroadcastLayout';
import { AnalyticsBox } from '../components/BroadcastSidebar/AnalyticsBox/AnalyticsBox';
import { SidebarBox } from '../components/BroadcastSidebar/SidebarBox/SidebarBox';
import SidebarFields from '../components/BroadcastSidebar/SidebarFields/SidebarFields';
import { useSubscribers } from '../hooks/useSubscribers';
import { selectBroadcast, selectIsDraft } from '../redux/selectors';

import styles from './BroadcastLogs.module.scss';

interface RowProps {
  row: { original: Subscriber };
}
export const BroadcastLogs = () => {
  const broadcast = useSelector(selectBroadcast);
  const [clickedSessions, setClickedSessions] = useState({});
  const [urlParams] = useSearchParams();
  const sessionId = urlParams.get('session_id');
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const broadcastId = useSelector(selectBroadcastId);
  const isDraft = useSelector(selectIsDraft);
  const [filter, setFilter] = useState('');
  const [filterStatus, setFilterStatus] = useState(null);
  const { handleAccountReplayClick } = useModalTryIt(sessionId);

  const handleBackClick = useCallback(() => {
    if (isDraft) {
      navigate(`/${slug}/broadcasts/draft`);
    } else {
      navigate(`/${slug}/broadcasts/${broadcastId}`);
    }
  }, [broadcastId, isDraft, navigate, slug]);

  const handleVisitSession = useCallback(() => {
    setClickedSessions((prev) => ({ ...prev, [sessionId]: true }));
    handleAccountReplayClick();
  }, [handleAccountReplayClick, sessionId]);

  useEffect(() => {
    if (sessionId) {
      handleVisitSession();
    }
  }, [handleVisitSession, sessionId]);

  const { t } = useTranslation();

  const { subscribers, hasNextPage, fetchNextPage, listStatus } = useSubscribers(
    broadcast?.broadcast_id,
    filter,
    filterStatus
  );

  useEffect(() => {
    if (broadcast?.broadcast_id && !broadcast?.sent_count && !broadcast?.failed_count) {
      navigate(`/${slug}/broadcasts/${broadcast?.broadcast_id}`);
    }
  }, [broadcast, navigate, slug]);

  const flatSubscribers = useMemo(
    () => [...(subscribers?.pages ?? [])].flatMap((page) => page.subscribers),
    [subscribers?.pages]
  );

  const getColumns = useMemo(() => {
    const cols: {
      Header: string | React.JSX.Element;
      Cell?: (data) => React.JSX.Element;
      accessor?: string;
      id?: string;
    }[] = [
      { Header: 'id', accessor: 'external_id' },
      {
        Header: t('broadcasts.recipients_status.sent'),
        Cell: (props: RowProps) => {
          const { sent_at: value, failed_at, status_code } = props.row.original;

          if (failed_at && status_code) {
            return (
              <div className="flex h-full w-full items-center">
                <ChannelErrorTooltip channel={IntegrationType.WHATSAPP} status_code={status_code}>
                  <span>
                    <Badge type="secondary" flavour="destructive">
                      {t('status.failed')}
                    </Badge>
                  </span>
                </ChannelErrorTooltip>
              </div>
            );
          }

          return <div className={styles.center}>{value ? formatDate(value, 'HH:mm') : '-'}</div>;
        },
      },
      {
        Header: t('broadcasts.recipients_status.delivered'),
        Cell: (props: RowProps) => {
          const value = props.row.original?.delivered_at;
          return <div className={styles.center}>{value ? formatDate(value, 'HH:mm') : '-'}</div>;
        },
      },
      {
        Header: t('broadcasts.recipients_status.read'),
        Cell: (props: RowProps) => {
          const value = props.row.original?.read_at;
          return <div className={styles.center}>{value ? formatDate(value, 'HH:mm') : '-'}</div>;
        },
      },
      {
        Header: t('broadcasts.recipients_status.replied'),
        Cell: (props: RowProps) => {
          const value = props.row.original?.replied_at;
          return <div className={styles.center}>{value ? formatDate(value, 'HH:mm') : '-'}</div>;
        },
      },
      {
        Header: t('common.replay'),
        Cell: (props: RowProps) => {
          const subscriber = props.row.original;
          const hasLog = subscriber?.session_id && subscriber.replied_at;
          const hasBeenClicked = clickedSessions[subscriber?.session_id];

          const getButtonIconColor = () => {
            if (hasLog && !hasBeenClicked) {
              return 'var(--color-background)';
            } else if (hasBeenClicked) {
              return 'var(--color-foreground-muted)';
            }
            return 'var(--color-foreground-muted)';
          };
          const isOlderThan6Months = isOlderThan(props.row.original.updated, 6);

          return (
            <InfoTooltip
              content={isOlderThan6Months ? t('analytics.old_conversation_tooltip') : ''}
              open={isOlderThan6Months ? undefined : false}
            >
              <Button
                disabled={!hasLog || isOlderThan6Months}
                variant={hasBeenClicked ? 'secondary-neutral' : 'primary'}
                size="icon-sm"
                onClick={() => handleAccountReplayClick(subscriber.session_id)}
              >
                <PlayIcon size={16} color={getButtonIconColor()} />
              </Button>
            </InfoTooltip>
          );
        },
      },
    ];

    return cols;
  }, [clickedSessions, handleAccountReplayClick, t]);

  const channel = broadcast.channel;

  const Icon = useMemo(() => integrationIconByType[channel] || (() => null), [channel]);

  const {
    subscribers_count = 0,
    read_count = 0,
    replied_count = 0,
    delivered_count = 0,
    sent_count = 0,
  } = broadcast || {};

  const analyticsData = [
    {
      key: 'sent_rate',
      label: t('broadcasts.recipients_status.sent'),
      percentage: clampPercentage(sent_count, subscribers_count),
      value: `${sent_count}/${subscribers_count}`,
    },
    {
      key: 'delivered_rate',
      label: t('broadcasts.recipients_status.delivered'),
      percentage: clampPercentage(delivered_count, sent_count),
      value: `${delivered_count}/${sent_count}`,
    },
    {
      key: 'open_rate',
      label: t('broadcasts.open_rate'),
      percentage: clampPercentage(read_count, delivered_count),
      value: `${read_count}/${delivered_count}`,
    },

    {
      key: 'reply_rate',
      label: t('broadcasts.reply_rate'),
      percentage: clampPercentage(replied_count, read_count),
      value: `${replied_count}/${read_count}`,
    },
  ];

  const filterOptions = useMemo(
    () => [
      { label: t('roles.filters.all'), value: null },
      { label: t('broadcasts.recipients_status.failed'), value: 'failed' },
      { label: t('broadcasts.recipients_status.delivered'), value: 'delivered' },
      { label: t('broadcasts.recipients_status.read'), value: 'read' },
      { label: t('broadcasts.recipients_status.replied'), value: 'replied' },
    ],
    [t]
  );

  return (
    <>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/${slug}/broadcasts/${broadcastId}`} className="breadcrumb-link">
                      <div className="flex items-center">
                        <div className="flex h-8 w-8 items-center justify-center">
                          <ArrowLeftIcon size={16} className="text-foreground-muted" />
                        </div>
                        <div className="mr-4 ml-2 h-4 w-px bg-border" />
                        {t('main_navigation.broadcast_other')}
                      </div>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <button onClick={handleBackClick} className="breadcrumb-link">
                      {broadcast?.name}
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t('broadcasts.logs')}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
      />
      <PageContentWrapper>
        <BroadcastLayout>
          <BroadcastLayout.Main>
            <div className={styles.charts}>
              {analyticsData.map(({ key, ...rest }) => (
                <AnalyticsBox key={key} {...rest} />
              ))}
            </div>
            <PlainFieldset fullWidth overflown>
              <TableInfiniteLoader fetchNextPage={fetchNextPage} hasNextPage={hasNextPage}>
                <div className={styles.tableContainer}>
                  <Table
                    data={flatSubscribers}
                    columns={getColumns}
                    sortable
                    searchable={false}
                    filterable
                    noGutters
                    onAPISearch={setFilter}
                    loading={listStatus === 'pending'}
                    filterOptions={filterOptions}
                    onFilterChange={setFilterStatus}
                  />
                </div>
              </TableInfiniteLoader>
            </PlainFieldset>
          </BroadcastLayout.Main>

          <BroadcastLayout.Sidebar>
            <SidebarBox>
              <SidebarFields
                validSubscribersCount={broadcast.subscribers_count}
                errorsCount={broadcast.failed_count}
              />
              {broadcast?.status === 'sent' && (
                <div className="text-left">
                  <Alert variant="default" className="gap-y-2 border-green-600 text-green-600">
                    <CircleCheckIcon size={16} />
                    <AlertTitle className="line-clamp-none text-sm leading-5 font-semibold tracking-normal">
                      {t('broadcasts.banner_sent', {
                        0: formatDate(broadcast.updated, 'yyyy-MM-dd h:mma'),
                      })}
                    </AlertTitle>
                  </Alert>
                </div>
              )}
              <div className={styles.channelContainer}>
                <span className="text-sm leading-5 font-medium text-foreground-muted">
                  {t('broadcasts.channel')}
                </span>
                <Icon size={24} />
                <span className="text-base leading-6 font-normal">{t(getChannelKey(channel))}</span>
              </div>
            </SidebarBox>
          </BroadcastLayout.Sidebar>
        </BroadcastLayout>
      </PageContentWrapper>
    </>
  );
};
